import { Serie } from "@nivo/line";
import {
  ANIMAL_SUPERGROUP_LABEL,
  ANIMAL_SUPERGROUP_SENTINEL,
  ANTIBIOTIC_GROUP_PREFIX,
  DEFAULT_ANTIBIOTIC_GROUP_LABEL,
} from "../constants";
import { AntibioticGroupOption } from "../types";

export const sanitizeOptionLabel = (value?: string | null) =>
  (value ?? "").trim();

export const arraysAreEqual = (a: string[], b: string[]) =>
  a.length === b.length && a.every((value, index) => value === b[index]);

export const isAntibioticGroupOption = (option: string) =>
  option.startsWith(ANTIBIOTIC_GROUP_PREFIX);

export const getGroupLabelFromOption = (option: string) =>
  option.slice(ANTIBIOTIC_GROUP_PREFIX.length);

export const getAntibioticDisplayLabel = (option: string) =>
  isAntibioticGroupOption(option)
    ? getGroupLabelFromOption(option)
    : option;

export const flattenAntibioticOptions = (
  groups?: AntibioticGroupOption[]
): string[] => {
  if (!Array.isArray(groups) || groups.length === 0) {
    return [];
  }

  const flattened: string[] = [];

  groups.forEach((entry) => {
    if (!entry) {
      return;
    }
    const groupLabel =
      sanitizeOptionLabel(entry.group) || DEFAULT_ANTIBIOTIC_GROUP_LABEL;
    const antibiotics = (entry.antibiotics ?? [])
      .map((value) => sanitizeOptionLabel(value))
      .filter((value): value is string => Boolean(value));

    if (antibiotics.length === 0) {
      return;
    }

    flattened.push(`${ANTIBIOTIC_GROUP_PREFIX}${groupLabel}`);
    flattened.push(...antibiotics);
  });

  return flattened;
};

export const buildSubgroupOptionValue = (animal: string, subgroup: string) =>
  `${animal}::${subgroup}`;

export const flattenSubgroupOptions = (
  options: Record<string, string[]>
): string[] => {
  const flattened: string[] = [];
  const seen = new Set<string>();
  Object.entries(options).forEach(([animalRaw, subgroups]) => {
    const animal = sanitizeOptionLabel(animalRaw);
    if (!animal) {
      return;
    }
    const entries = [
      buildSubgroupOptionValue(animal, ANIMAL_SUPERGROUP_SENTINEL),
      ...(subgroups ?? [])
        .map((value) => sanitizeOptionLabel(value))
        .filter((value): value is string => Boolean(value))
        .map((subgroup) => buildSubgroupOptionValue(animal, subgroup)),
    ];
    entries.forEach((entry) => {
      if (!seen.has(entry)) {
        seen.add(entry);
        flattened.push(entry);
      }
    });
  });
  return flattened;
};

export const formatSubgroupLabel = (value: string) => {
  const [animal, subgroup] = value.split("::");
  if (!animal || !subgroup) {
    return value;
  }
  if (subgroup === ANIMAL_SUPERGROUP_SENTINEL) {
    return `${animal} (${ANIMAL_SUPERGROUP_LABEL})`;
  }
  return `${animal} - ${subgroup}`;
};

export const sortSeriesByYear = (series: Serie[]): Serie[] =>
  series.map((entry) => ({
    ...entry,
    data: [...entry.data].sort((a, b) => {
      const aYear = typeof a.x === "number" ? a.x : Number(a.x);
      const bYear = typeof b.x === "number" ? b.x : Number(b.x);
      return aYear - bYear;
    }),
  }));
